/**
 * RocketLawyer interview `showIf` conditional-visibility evaluator (RAC-10239).
 *
 * RocketLawyer returns a per-QUESTION `showIf` string that gates whether the
 * question (and its fields) should display, based on another field's answer on
 * the same page — the "answer Yes → follow-up fields appear" behavior. Fields
 * themselves carry no `showIf`; it is question-scoped only.
 *
 * Observed grammar (sandbox capture of every state lease, RAC-10160):
 *   - atomic:   `(<fieldId> = true)`
 *   - compound: `((A = true) AND (B = true))`  — parenthesized, AND/OR joined
 * Only `=` and `true` were seen in the wild, but this parser also handles `!=`,
 * `false`, quoted/bare string values, and `OR` so a richer template can't break
 * us. Anything it cannot parse FAILS OPEN (question shown) so we never hide a
 * question RocketLawyer intended the user to answer.
 *
 * Pure + framework-free on purpose: no Vue imports, so it is unit-testable in
 * isolation and verifiable against the captured page fixtures.
 */

const TOKEN_PATTERN = /\s*(\(|\)|!=|=|"[^"]*"|[^\s()]+)/y;

/**
 * @param {string} condition
 * @returns {string[]}
 */
function tokenize(condition) {
    const tokens = [];
    TOKEN_PATTERN.lastIndex = 0;
    let match;
    while ((match = TOKEN_PATTERN.exec(condition)) !== null) {
        tokens.push(match[1]);
        if (TOKEN_PATTERN.lastIndex >= condition.length) {
            break;
        }
    }
    return tokens;
}

/**
 * Coerce a literal token ("true" | "false" | "\"text\"" | bare) to a JS value.
 *
 * @param {string} token
 * @returns {boolean|string}
 */
function literal(token) {
    if (token === 'true') {
        return true;
    }
    if (token === 'false') {
        return false;
    }
    if (token.length >= 2 && token.startsWith('"') && token.endsWith('"')) {
        return token.slice(1, -1);
    }
    return token;
}

/**
 * Compare a stored answer to an expected literal. A RADIO/CHECKBOX answer is a
 * boolean in `answers`; an unselected option is absent (undefined) and treated
 * as `false`. String values compare stringwise.
 *
 * @param {*} actual
 * @param {boolean|string} expected
 * @returns {boolean}
 */
function valuesEqual(actual, expected) {
    if (typeof expected === 'boolean') {
        return (actual === true) === expected;
    }
    return String(actual ?? '') === String(expected);
}

/**
 * Recursive-descent evaluator over the token stream. Each parenthesized group
 * is either an atom `( id op value )` or a binary `( expr AND|OR expr )`.
 *
 * @param {string[]} tokens
 * @param {{ pos: number }} cursor
 * @param {Record<string, *>} answers
 * @returns {boolean}
 */
function parseExpression(tokens, cursor, answers) {
    if (tokens[cursor.pos] !== '(') {
        throw new Error('Expected "(" at position ' + cursor.pos);
    }
    cursor.pos++; // consume '('

    let value;

    if (tokens[cursor.pos] === '(') {
        // Compound: ( <expr> AND|OR <expr> )
        const left = parseExpression(tokens, cursor, answers);
        const operator = (tokens[cursor.pos] || '').toUpperCase();
        if (operator !== 'AND' && operator !== 'OR') {
            throw new Error('Expected AND/OR, got ' + operator);
        }
        cursor.pos++; // consume operator
        const right = parseExpression(tokens, cursor, answers);
        value = operator === 'AND' ? left && right : left || right;
    } else {
        // Atom: ( <fieldId> = | != <value> )
        const fieldId = tokens[cursor.pos++];
        const comparator = tokens[cursor.pos++];
        const expected = literal(tokens[cursor.pos++]);
        if (comparator !== '=' && comparator !== '!=') {
            throw new Error('Unexpected comparator ' + comparator);
        }
        const equal = valuesEqual(answers[fieldId], expected);
        value = comparator === '=' ? equal : !equal;
    }

    if (tokens[cursor.pos] !== ')') {
        throw new Error('Expected ")" at position ' + cursor.pos);
    }
    cursor.pos++; // consume ')'

    return value;
}

/**
 * Evaluate a single `showIf` condition against the current answers.
 * Empty / missing condition → always visible. Unparseable → fail open (visible).
 *
 * @param {?string} condition
 * @param {Record<string, *>} answers
 * @returns {boolean}
 */
export function evaluateShowIf(condition, answers) {
    if (condition === null || condition === undefined || String(condition).trim() === '') {
        return true;
    }

    try {
        const tokens = tokenize(String(condition).trim());
        if (tokens.length === 0) {
            return true;
        }
        const cursor = { pos: 0 };
        const result = parseExpression(tokens, cursor, answers || {});
        // Trailing tokens mean we mis-parsed — don't trust a partial result.
        if (cursor.pos !== tokens.length) {
            return true;
        }
        return result;
    } catch {
        return true;
    }
}

/**
 * Filter a page's questions to those currently visible, in document order, with
 * cascade: a hidden question's fields are removed from the working answer set so
 * a stale value can't reveal a downstream question that depends on it.
 *
 * @param {Array<{ showIf?: string, fields?: Array<{ id: string }> }>} questions
 * @param {Record<string, *>} answers
 * @returns {Array<object>}
 */
export function visibleQuestions(questions, answers) {
    const list = Array.isArray(questions) ? questions : [];
    const effective = { ...(answers || {}) };
    const visible = [];

    for (const question of list) {
        if (evaluateShowIf(question?.showIf, effective)) {
            visible.push(question);
        } else {
            for (const field of question?.fields || []) {
                if (field && field.id != null) {
                    delete effective[field.id];
                }
            }
        }
    }

    return visible;
}
